import React, { ComponentProps, forwardRef } from "react";
import { View, StyleSheet, StyleProp, ViewStyle } from "react-native";
import { BasquiatPaddingProps, useUnitPropsConverter } from "./units";
import Gap from "./Gap";

type VStackAlignment = "leading" | "center" | "trailing" | "stretch";

export type VStackProps = {
    alignment?: VStackAlignment;
    gap?: number | string;
    style?: StyleProp<ViewStyle>;
    children: React.ReactNode;
    onLayout?: ComponentProps<typeof View>["onLayout"];
} & BasquiatPaddingProps;

const ALIGNMENT_MAP: { [key: string]: StyleProp<ViewStyle> } = {
    leading: { alignItems: "flex-start" },
    center: { alignItems: "center" },
    trailing: { alignItems: "flex-end" },
    stretch: { alignItems: "stretch" },
};

const styles = StyleSheet.create({
    vStack: {
        flexDirection: "column",
    },
});

// eslint-disable-next-line react/display-name
export const VStack = forwardRef<View, VStackProps>(
    (
        {
            gap,
            alignment = "stretch",
            style,
            children,
            onLayout,
            ...paddingProps // BasquiatPaddingProps
        },
        ref,
    ) => {
        const filteredChildren = React.Children.toArray(children).filter((child) => !!child);
        const childrenCount = filteredChildren.length;
        const propsConverter = useUnitPropsConverter();
        const paddingStyle = propsConverter(paddingProps);
        return (
            <View
                ref={ref}
                style={[styles.vStack, paddingStyle, ALIGNMENT_MAP[alignment], style]}
                onLayout={onLayout}
            >
                {gap && gap !== 0 && childrenCount > 1
                    ? filteredChildren.map((child, index) => (
                          <React.Fragment key={index}>
                              {child}
                              {child && index < childrenCount - 1 ? (
                                  <Gap gap={gap} mode="vertical" />
                              ) : null}
                          </React.Fragment>
                      ))
                    : children}
            </View>
        );
    },
);

export default VStack;
